import { z } from 'zod';
import { DisplayReferenceSchema, UUIDSchema } from '../domain/models';
import {
  type PaginationQueryParams,
  PaginationSchema,
} from '../domain/pagination';
import { PaginatedBundleSchema, QuantitySchema } from '../domain/schema';

const Status = [
  'planned',
  'billable',
  'not-billable',
  'aborted',
  'billed',
  'entered-in-error',
  'unknown',
] as const;

/**
 * Used for tracking and management of consumables - linked to the FHIR chargeItem resource
 */
export const ConsumableSchema = z.object({
  medpraxCode: z.string(),
  nappiCode: z.string(),
  name: z.string(),
  quantity: QuantitySchema,
  patient: DisplayReferenceSchema,
  admission: DisplayReferenceSchema,
  dateOfUse: z.string(),
  performer: DisplayReferenceSchema,
  status: z.enum(Status),
});

export const ConsumableCreateInputRequestSchema = z.object({
  medpraxCode: z.string(),
  dateOfUse: z.string(),
  quantity: z.number(),
  name: z.string().optional(),
  nappiCode: z.string().optional(),
});
export type ConsumableCreateInputRequest = z.infer<
  typeof ConsumableCreateInputRequestSchema
>;

export const ConsumableCreateRequestSchema = z.object({
  consumables: ConsumableCreateInputRequestSchema.array(),
  patient: UUIDSchema,
  admission: UUIDSchema,
});
export type ConsumableCreateRequest = z.infer<
  typeof ConsumableCreateRequestSchema
>;

export const ConsumableGetAllRequestSchema = PaginationSchema.extend({
  patientId: UUIDSchema,
});

export type ConsumableGetAllRequest = z.infer<
  typeof ConsumableGetAllRequestSchema
>;

export const ConsumableUsageSchema = ConsumableSchema.pick({
  name: true,
  dateOfUse: true,
  performer: true,
  status: true,
}).extend({
  id: UUIDSchema,
  quantity: z.number(),
});

export const ConsumableGetAllResponseSchema = PaginatedBundleSchema.extend({
  consumableUsages: ConsumableUsageSchema.partial().array(),
});

export const ConsumableSearchParamsSchema = z.object({
  query: z.string().optional(),
});
export type ConsumableSearchParams = z.infer<
  typeof ConsumableSearchParamsSchema
>;

export type ConsumableGetAllResponse = z.infer<
  typeof ConsumableGetAllResponseSchema
>;

export const ConsumableGetResponseSchema = ConsumableUsageSchema.partial();
export type ConsumableGetResponse = z.infer<typeof ConsumableGetResponseSchema>;

export type Consumable = z.infer<typeof ConsumableSchema>;

export type ConsumableQueryParams = PaginationQueryParams;

export const ConsumableUpdateRequestSchema = z.object({
  id: UUIDSchema,
  status: z.enum(Status),
});
export type ConsumableUpdateRequest = z.infer<
  typeof ConsumableUpdateRequestSchema
>;

export type ConsumableUpdateResponse = ConsumableGetResponse;

export type ConsumableUsage = z.infer<typeof ConsumableUsageSchema>;

export const ConsumableSearchResponseSchema = z.object({
  medpraxCode: z.string(),
  name: z.string().optional(),
  nappiCode: z.string().optional(),
});

export type ConsumableSearchResponse = z.infer<
  typeof ConsumableSearchResponseSchema
>;

export const quroConsumableCodes = [
  'C80Q',
  'S18O',
  'T2JW',
  'R2A3',
  'B9DE',
  'G2IJ',
  '11F2',
  '5URI',
  'E733',
  'MVG9',
  'Q8FK',
  'M83M',
  'I2JI',
  'M9BM',
  '4V52',
  'R2B0',
  '4COM',
  'B5AY',
  '2ADR',
  '2ATR',
  'VB1C',
  'I6A8',
  'I5LV',
  '7UG2',
  '235X',
  'I7S1',
  '48X3',
  '1TG6',
  '8DEX',
  'AK20',
  'O861',
  'SOD6',
  'SOD9',
  'SOE4',
  '800E',
  'A01I',
  'I05Q',
  'GL12',
  'G0PC',
  '9TYU',
  'F3MP',
  '1LL7',
  '1JLO',
  'G667',
  'C2C5',
  'C1C5',
  '1R97',
  '1R98',
  '378I',
  'G3UA',
  'QLT4',
  '466S',
  'G4DM',
  'R9DP',
  'Q8FK',
  '5CH4',
  'G527',
  '2SO0',
  'G528',
  'SA17',
  '5NOR',
  '3SOD',
  '3NO3',
  '1R07',
  '940R',
  'ROI1',
  '0DHJ',
  'G2PX',
  'Q4MZ',
  'YP91',
  '11H4',
  'M83M',
  'JHJ1',
  'W9DZ',
  'H240',
  '3SAB',
  'C1KQ',
  '3W50',
  'A1U7',
  'T17B',
  '0DIR',
  'G2PR',
  'B1YG',
  '12P0',
  '01Z5',
  'HUI7',
  '2TEG',
  '4TEG',
  '1TEG',
  '4COM',
  'B35L',
  '5URI',
  'PPE2',
];
